import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Fingerprint, Loader2, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { inputCls } from "../forms/FormBits";
import { passkeySupported, registerPasskey } from "../../lib/webauthn";

/* Primary CEO only: real WebAuthn enrolment (origin-bound; production requires separate enrolment). */
export default function CeoPasskeys() {
  const [data, setData] = useState(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/ceo/webauthn/credentials").then((r) => setData(r.data)).catch(() => setData({ configured: false, credentials: [] }));
  useEffect(() => { load(); }, []);

  const add = async () => {
    setBusy(true);
    try { await registerPasskey(label || "Passkey"); setLabel(""); toast.success("Passkey enrolled"); load(); }
    catch (e) { toast.error(e.response ? formatApiErrorDetail(e.response.data?.detail) : e.message); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this passkey? You will need another method to enter the Control Room.")) return;
    try { await api.delete(`/ceo/webauthn/credentials/${id}`); toast.success("Passkey removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!data) return <Loader2 className="h-4 w-4 animate-spin text-gold" />;
  return (
    <div className="space-y-3" data-testid="ceo-passkeys">
      <p className="text-xs text-muted-foreground">Passkeys are bound to this domain{data.rp_id ? ` (${data.rp_id})` : ""}. After deploying to production you enrol again there.</p>
      {!data.configured && <p className="rounded bg-amber-50 p-2 text-xs text-amber-800" data-testid="ceo-passkeys-unconfigured">Passkey verification is not configured for this environment (WEBAUTHN_RP_ID / WEBAUTHN_ORIGIN).</p>}
      <ul className="divide-y divide-border rounded-lg border border-border" data-testid="ceo-passkey-list">
        {data.credentials.length === 0 && <li className="p-3 text-sm text-muted-foreground" data-testid="ceo-passkeys-empty">No passkey enrolled. TOTP is the active fallback.</li>}
        {data.credentials.map((c) => (
          <li key={c.id} className="flex items-center justify-between gap-3 p-3 text-sm" data-testid={`ceo-passkey-${c.id.slice(0, 8)}`}>
            <div><p className="font-semibold text-navy">{c.label}</p><p className="text-xs text-muted-foreground">{c.device_type}{c.backed_up ? " · synced" : ""} · added {c.created_at?.slice(0, 10)}{c.last_used_at ? ` · last used ${c.last_used_at.slice(0, 10)}` : ""}</p></div>
            <button onClick={() => remove(c.id)} className="rounded p-2 text-destructive hover:bg-destructive/10" aria-label="Remove passkey" data-testid={`ceo-passkey-remove-${c.id.slice(0, 8)}`}><Trash2 className="h-4 w-4" /></button>
          </li>
        ))}
      </ul>
      {data.configured && passkeySupported() && (
        <div className="flex gap-2">
          <input className={inputCls} placeholder="Label (e.g. MacBook Touch ID)" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="ceo-passkey-label" />
          <button onClick={add} disabled={busy} className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-navy px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60" data-testid="ceo-passkey-add">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Fingerprint className="h-4 w-4" />} Add passkey
          </button>
        </div>
      )}
    </div>
  );
}
